import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { useAppDispatch } from '../../app/redux/hooks';
import LoadingModal from '../../common/components/loading-modal/LoadingModal';
import { useAddSplitTransactionMutation, useDoneSplitTransactionMutation, useMoveItemSplitTransactionMutation } from '../api';
import { showErrorDialog } from '../error-dialog/errorDialogSlice';
import { setTransaction, useTransaction } from '../order/orderSlice';
import { setActiveTransactionId } from '../tender/tenderSlice';

import SplitOrder from './SplitOrder';
import { clearSplit, setVisibleSplitIndex, useActiveTransactionLineItem, useVisibleSplitIndex } from './splitSplice';

import './styles.sass';

function Split() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const visibleSplitIndex = useVisibleSplitIndex();
  const activeTransactionLineItem = useActiveTransactionLineItem();

  const transactions = useTransaction();
  const [triggerAddSplit] = useAddSplitTransactionMutation();
  const [triggerMoveItem] = useMoveItemSplitTransactionMutation();
  const [triggerSplitDone] = useDoneSplitTransactionMutation();

  const [showConfirm, setShowConfirm] = useState(false);
  const [isMovingItem, setIsMovingItem] = useState(false);
  const [doing, setDoing] = useState(false);
  const [tooManySplits, setTooManySplits] = useState(false);
  const [showSplitTooManyError, setShowSplitTooManyError] = useState(false);

  const onDoneClick = () => {
    if (transactions && transactions.length > 0) {
      setDoing(true);
      triggerSplitDone(transactions[0].id)
        .unwrap()
        .then((data) => {
          dispatch(clearSplit());
          dispatch(setTransaction(data));
          dispatch(setActiveTransactionId(data[0].id));
          navigate('/tender');
        })
        .catch(() =>
          dispatch(
            showErrorDialog({
              message: 'An error occurred completing split',
              dismissible: true,
              buttonText: 'OK',
            }),
          ),
        );
    }
  };

  useEffect(() => {
    if (transactions) {
      if (visibleSplitIndex === 0 && transactions.length > 1) dispatch(setVisibleSplitIndex(1));
      if (transactions.length > 8) setTooManySplits(true);
    }
  }, [transactions, dispatch, visibleSplitIndex]);

  if (transactions === undefined) return <LoadingModal />;

  const addSplit = () => {
    if (tooManySplits) {
      setShowSplitTooManyError(true);
      return;
    }

    if (showConfirm === false) {
      setShowConfirm(true);
      return;
    }

    setShowConfirm(false);
    setIsMovingItem(true);
    triggerAddSplit(transactions[0].id)
      .unwrap()
      .then((data) => {
        dispatch(setTransaction(data));
        setIsMovingItem(false);
      })
      .catch((err) => {
        dispatch(showErrorDialog({ message: err, dismissible: true }));
        setIsMovingItem(false);
      });
  };

  const moveItemLeft = () => {
    if (activeTransactionLineItem) {
      setIsMovingItem(true);
      triggerMoveItem({
        transactionLineItemId: activeTransactionLineItem.id,
        newTransactionId: transactions[0].id,
      })
        .unwrap()
        .then((data) => {
          dispatch(setTransaction(data));
          setIsMovingItem(false);
        })
        .catch((err) => {
          if (err.status === 424) dispatch(showErrorDialog({ message: err.data.error, dismissible: true }));
          else dispatch(showErrorDialog({ message: JSON.stringify(err), dismissible: true }));
          setIsMovingItem(false);
        });
    }
  };
  const moveItemRight = () => {
    if (activeTransactionLineItem) {
      if (transactions[0].transactionLineItems.length > 1) {
        setIsMovingItem(true);
        triggerMoveItem({
          transactionLineItemId: activeTransactionLineItem.id,
          newTransactionId: transactions[visibleSplitIndex].id,
        })
          .unwrap()
          .then((data) => {
            dispatch(setTransaction(data));
            setIsMovingItem(false);
          })
          .catch((err) => {
            dispatch(showErrorDialog({ message: JSON.stringify(err), dismissible: true }));
            setIsMovingItem(false);
          });
      } else {
        dispatch(
          showErrorDialog({
            message: 'You must have at least one item left on the main transaction',
            dismissible: true,
          }),
        );
      }
    }
  };

  return (
    <div className="pos-container pos-container_role_split">
      <div className="pos-split">
        <SplitOrder splitNumber={1} transaction={transactions[0]} totalSplits={transactions.length} />
        <div className="pos-split__dashboard">
          <div className="pos-split__actions">
            <button className="pos-split__action pos-split__action_role_add" type="button" onClick={addSplit}>
              + Another Split
            </button>
          </div>
          <div className="pos-split__controls">
            <button
              className="pos-split__control pos-split__control_role_add-to"
              type="button"
              onClick={moveItemRight}
              disabled={
                activeTransactionLineItem === undefined ||
                transactions[0].transactionLineItems.find((x) => x.id === activeTransactionLineItem.id) === undefined
              }
            >
              Add to Split
            </button>
            <button
              className="pos-split__control pos-split__control_role_take-off"
              type="button"
              onClick={moveItemLeft}
              disabled={
                activeTransactionLineItem === undefined ||
                transactions[visibleSplitIndex].transactionLineItems.find((x) => x.id === activeTransactionLineItem.id) === undefined
              }
            >
              Take off Split
            </button>
          </div>
          <div className="pos-split__actions">
            <button disabled={isMovingItem} className="pos-split__action pos-split__action_role_done" type="button" onClick={onDoneClick}>
              Done
            </button>
          </div>
        </div>
        {transactions.length === 1 && <div className="pos-split__order"></div>}
        {transactions.length > 1 && (
          <SplitOrder transaction={transactions[visibleSplitIndex]} splitNumber={visibleSplitIndex + 1} totalSplits={transactions.length} />
        )}
      </div>
      {doing && <LoadingModal />}
      {showConfirm && (
        <div className="pos-modal-portal">
          <div className={`pos-modal-overlay`} style={{ zIndex: 999 }} onClick={() => setShowConfirm(false)}>
            <div className="pos-popup pos-popup_role_info">
              <div className="pos-popup__content">
                <div className="pos-popup__icon"></div>
                <div className="pos-popup__info">
                  <p className="pos-popup__text">
                    You are now creating a new Split
                    <br />
                    <br />
                    Any deals on the current transaction may be lost!
                  </p>
                </div>
                <div className="pos-popup__actions">
                  <button className="pos-popup__action" onClick={addSplit}>
                    Add Split
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      {showSplitTooManyError && (
        <div className="pos-modal-portal">
          <div className={`pos-modal-overlay`} style={{ zIndex: 999 }} onClick={() => setShowConfirm(false)}>
            <div className="pos-popup pos-popup_role_error">
              <div className="pos-popup__content">
                <div className="pos-popup__icon"></div>
                <div className="pos-popup__info">
                  <p className="pos-popup__text">
                    You can only have 8 splits at a time.
                    <br />
                    <br />
                    Please pay off a split before adding another.
                  </p>
                </div>
                <div className="pos-popup__actions">
                  <button className="pos-popup__action" onClick={() => setShowSplitTooManyError(false)}>
                    OK
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Split;
